import { Dayjs } from "dayjs";
import { useMemo } from "react";
import { DayIndicator } from "components/DateStepper";
import { useEventFilters } from "components/manualReview/contexts/EventFilterContext";
import {
  CONSUMPTION_EVENT_FILTER,
  HAS_COMMENT_FILTER,
  requestedReviewReasons,
  UNFLAGGED_REVIEW_REASON,
} from "components/manualReview/filters/eventFilters";
import {
  ReviewCountCategory,
  ReviewPeriodStatusCounts,
  ReviewReason,
  useGetReviewMonthSummaryQuery,
} from "src/store/api/generatedApi";
import { useTenant } from "hooks/settings";

const pickStatusColour = (
  counts: ReviewPeriodStatusCounts
): string | undefined => {
  const total =
    counts.verified +
    counts.assigned_to_me +
    counts.assigned_to_other +
    counts.unassigned;
  if (total === 0) return undefined;
  if (counts.verified === total) return undefined;
  if (counts.assigned_to_me > 0) return "info.main";
  if (counts.unassigned > 0) return "error.main";
  return "warning.main";
};

export const useReviewMonthIndicators = ({
  location,
  month,
}: {
  location: string | undefined;
  month: Dayjs;
}): {
  indicators: Map<string, DayIndicator> | undefined;
  isFetching: boolean;
} => {
  const tenantName = useTenant();
  const {
    filters,
    consumptionLimitTo,
    materialAreaFilters,
    reviewReasonFilters,
  } = useEventFilters();
  const monthStart = month.startOf("month").format("YYYY-MM-DD");
  const monthEnd = month.endOf("month").format("YYYY-MM-DD");

  const reviewReasons = useMemo<ReviewReason[]>(
    () =>
      requestedReviewReasons(
        reviewReasonFilters.filter(
          (filter) => filter.reason !== UNFLAGGED_REVIEW_REASON
        )
      ),
    [reviewReasonFilters]
  );

  const includeCountCategories = useMemo<ReviewCountCategory[]>(() => {
    const categories: ReviewCountCategory[] = [];
    if (filters.includes(HAS_COMMENT_FILTER)) categories.push("commented");
    if (filters.includes(CONSUMPTION_EVENT_FILTER))
      categories.push("consumption");
    return categories;
  }, [filters]);

  const materialAreaFilterList = useMemo(
    () => ({
      items: materialAreaFilters.map((filter) => ({
        material: filter.material,
        area: filter.area,
        event_type: filter.eventType,
        limit_to: filter.limitTo,
        negated: filter.negated ?? [],
      })),
    }),
    [materialAreaFilters]
  );

  const { data, isFetching } = useGetReviewMonthSummaryQuery(
    {
      tenantName,
      location: location ?? "",
      monthStart,
      monthEnd,
      includeCountCategories:
        includeCountCategories.length > 0 ? includeCountCategories : undefined,
      consumptionLimitTo,
      materialAreaFilterList,
      reviewReasons: reviewReasons.length > 0 ? reviewReasons : undefined,
    },
    { skip: !location }
  );

  const indicators = useMemo(() => {
    if (!data) return undefined;
    const indicatorsByDate = new Map<string, DayIndicator>();
    for (const day of data.days) {
      const count = day.filtered_event_count;
      indicatorsByDate.set(day.date, {
        eventCount: count != null && count > 0 ? count : undefined,
        statusColour: pickStatusColour(day.review_period_counts),
      });
    }
    return indicatorsByDate;
  }, [data]);

  return { indicators, isFetching };
};
